import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { useAuth } from "@/auth/AuthProvider";
import { TextField } from "@/components/TextField";
import { PhoneListField } from "@/components/PhoneListField";
import { SelectField, type SelectOption } from "@/components/SelectField";
import {
  listIndustries,
  listConstructionManagementSystems,
  listBrokerageBrokers,
  accountFloorError,
  type AccountInput,
  type AccountRecord,
  type Address,
  type EntityType,
  type PersonnelInput,
  accountWebsite,
} from "@/lib/brokerAccounts";
import {
  toPhoneValues,
  toPhoneInputs,
  phoneRowErrors,
  type PhoneValue,
} from "@/lib/phone";
import {
  PERSONNEL_ROLES,
  PERSONNEL_ROLE_LABELS,
  normalizeRoles,
  type PersonnelRole,
} from "@/lib/personnelRoles";
import { missingFieldsOf } from "@/lib/submissions/brokerSubmissions";
import { isLocationCountyKey, type MissingField } from "@/lib/brokerProducts";
import { messageFor } from "@/lib/errors";
import { AddressFieldset } from "@/components/AddressFieldset";
import {
  EMPTY_ADDRESS,
  isCompleteAddress,
  type AddressValue,
} from "@/lib/address";
import {
  ExtractedMarker,
  ExtractedReviewBanner,
} from "@/components/ExtractedReview";
import { useExtractedReview } from "@/lib/useExtractedReview";
import { ENTITY_TYPE_OPTIONS } from "@/lib/entityTypes";
import {
  ACCOUNT_FORM_ADDRESS,
  accountAddress,
  carriedFrom,
  carriedInput,
  parseDecimal as parseShare,
  type CarriedPerson,
} from "@/lib/accountWrite";

import { buttonClass } from "@/components/buttonStyles";
// The shared account form (BRK-S033), used for BOTH create (AddAccountPage) and
// edit (AccountCard) so the field set, validation, and pickers live in one place.
//
// BRK-S057 adds a STAGE-AWARE floor mode for the submission flow: pass
// `floorFieldKeys` (the product manifest's account-scope indication-floor field
// keys — address only for WC) and the form requires ONLY those, moving every
// other field behind an "Add more detail" disclosure so the step-2 surface stays
// minimal. Omit `floorFieldKeys` for the standalone /accounts pages, which keep
// the legacy full floor (name, email, phone, address, ≥1 person). Either way the
// form submits a PARTIAL payload — blank optional fields are omitted, never sent
// as "" (the server 400s empty strings; a partial PUT leaves them unchanged).
const ENTITY_OPTIONS: SelectOption[] = ENTITY_TYPE_OPTIONS;

// The address value + blank + input live in the shared AddressFieldset now
// (BRK-S067); this alias keeps the local helper signatures readable.
type AddressForm = AddressValue;

// A personnel row as the FORM holds it: the wire shape's scalars plus editable
// phone rows (PMS-S173 made a person's numbers an array too — BRK-S073) and the
// person's roles (PMS-S178 — BRK-S081).
interface PersonForm {
  /** Present for a person LOADED from the account, absent for one the broker
   *  added. Carried, never edited — it is how the write says who this is
   *  (BRK-S095); see PersonnelInput.id for what happens without it. */
  id?: string;
  firstName: string;
  lastName: string;
  title?: string;
  email?: string;
  phones: PhoneValue[];
  roles: PersonnelRole[];
  /** Carried, never edited (there is no input for it): a PUT replaces the whole
   *  personnel set, so a share this form loaded must go back out or the server
   *  drops it — and the derived LLC member/manager count reads off it. */
  ownershipPercentage: number | null;
  /** Everything else the read served for a LOADED person, already in write
   *  shape. There is no input for any of it, but a PUT replaces each person
   *  wholesale, so it must go back out or the save nulls it (BRK-S223). */
  carried?: CarriedPerson;
}

const EMPTY_PERSON: PersonForm = {
  firstName: "",
  lastName: "",
  phones: toPhoneValues([]),
  roles: [],
  ownershipPercentage: null,
};

function toAddressForm(a: Address | null): AddressForm {
  if (!a) return { ...EMPTY_ADDRESS };
  return {
    street: a.street,
    city: a.city,
    state: a.state,
    zip: a.zip,
    ...(a.county ? { county: a.county } : {}),
  };
}

function sameAddress(a: AddressForm, b: AddressForm): boolean {
  return (
    a.street === b.street &&
    a.city === b.city &&
    a.state === b.state &&
    a.zip === b.zip
  );
}

export function AccountForm({
  initial,
  initialServicingBrokerId = null,
  withServicingBroker = false,
  floorFieldKeys,
  confirmStateAgainst,
  extractedKeys,
  submitLabel,
  pendingLabel,
  onSubmit,
  cancelSlot,
}: {
  /** Prefill for edit; omit for a blank create form. */
  initial?: AccountRecord;
  initialServicingBrokerId?: string | null;
  withServicingBroker?: boolean;
  /** Account field keys prefilled from an uploaded document (BRK-S038). Each is
   *  MARKED "from your document — confirm or edit" and cleared the first time the
   *  broker edits it; a review banner leads the form. Omit on the manual path
   *  (no markers). Keys match the field keys below (legalName, emailAddress,
   *  phones, entityType, ein, website, dbaName, legalAddress, personnel). */
  extractedKeys?: string[];
  /** Submission-flow stage floor (BRK-S057): the manifest account-scope field
   *  keys required at this step. When set, only these are required and every
   *  other field sits behind the "Add more detail" disclosure. Omit for the
   *  standalone /accounts pages (legacy full floor). */
  floorFieldKeys?: string[];
  /** The step-1 intake state to reconcile the entered address against
   *  (BRK-S059). When set and the submitted legal/mailing address state differs,
   *  the save pauses for an explicit confirmation — state drives appetite +
   *  rating, so a silent mismatch quietly produces wrong prices. Omit where no
   *  intake state exists (the standalone /accounts pages, renewals). */
  confirmStateAgainst?: string | null;
  submitLabel: string;
  pendingLabel: string;
  /** Persist the account; throwing surfaces the message inline. On success the
   *  parent navigates or swaps in the fresh detail (this form doesn't self-reset). */
  onSubmit: (input: AccountInput) => Promise<void>;
  /** Rendered beside the submit button (a Cancel link/button chosen by the caller). */
  cancelSlot?: ReactNode;
}) {
  const { identity, authedRequest } = useAuth();

  // BRK-S038 — document-extracted fields are marked for review until edited.
  const review = useExtractedReview(extractedKeys);

  const [legalName, setLegalName] = useState(initial?.legalName ?? "");
  const [dbaName, setDbaName] = useState(initial?.dbaName ?? "");
  const [email, setEmail] = useState(initial?.emailAddress ?? "");
  // The business's numbers (PMS-S173): at least one, exactly one primary.
  const [phones, setPhones] = useState<PhoneValue[]>(
    toPhoneValues(initial?.phones),
  );
  const [ein, setEin] = useState(initial?.ein ?? "");
  // `website` replaced `url` on the broker contract (PMS-S314). The read is
  // tolerant of either spelling (`accountWebsite`); the write sends only the new
  // one, and the field key is `website` so a document-prefill marker lands on it.
  const [website, setWebsite] = useState(
    initial ? (accountWebsite(initial) ?? "") : "",
  );
  const [entityType, setEntityType] = useState(initial?.entityType ?? "");
  const [industryId, setIndustryId] = useState(initial?.industryId ?? "");
  const [cmsId, setCmsId] = useState(
    initial?.constructionManagementSystemId ?? "",
  );
  const initialLegal = toAddressForm(initial?.legalAddress ?? null);
  const initialMailing = toAddressForm(initial?.mailingAddress ?? null);
  const [legal, setLegal] = useState<AddressForm>(initialLegal);
  const [mailing, setMailing] = useState<AddressForm>(initialMailing);
  const [mailingSame, setMailingSame] = useState(
    !initial?.mailingAddress || sameAddress(initialLegal, initialMailing),
  );
  const [personnel, setPersonnel] = useState<PersonForm[]>(
    initial?.personnel?.length
      ? initial.personnel.map((p) => ({
          id: p.id,
          firstName: p.firstName,
          lastName: p.lastName,
          ...(p.title ? { title: p.title } : {}),
          ...(p.email ? { email: p.email } : {}),
          phones: toPhoneValues(p.phones),
          // Normalized on the way in: a duplicate role would 400 on the way out.
          roles: normalizeRoles(p.roles ?? []),
          ownershipPercentage: parseShare(p.ownershipPercentage),
          carried: carriedFrom(p),
        }))
      : [{ ...EMPTY_PERSON, phones: toPhoneValues([]) }],
  );
  const [servicingBrokerId, setServicingBrokerId] = useState(
    initialServicingBrokerId ?? "",
  );

  const [industries, setIndustries] = useState<SelectOption[]>([]);
  const [cmsOptions, setCmsOptions] = useState<SelectOption[]>([]);
  const [brokerOptions, setBrokerOptions] = useState<SelectOption[]>([]);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Structured server field errors (BRK-S057, PMS-S144 shape) — rendered as a
  // per-field checklist instead of one generic banner.
  const [fieldErrors, setFieldErrors] = useState<MissingField[] | null>(null);
  // A pending state-mismatch confirmation (BRK-S059): the entered address state
  // vs the step-1 intake state. Non-null holds the save open until the broker
  // confirms or goes back.
  const [mismatch, setMismatch] = useState<{
    entered: string;
    expected: string;
  } | null>(null);
  // In floor mode the non-floor fields start hidden behind the disclosure.
  const [showExtras, setShowExtras] = useState(false);
  // Phone rejections are shown only after a submit attempt, then recomputed on
  // every keystroke so a corrected number clears its own message (BRK-S073).
  const [attempted, setAttempted] = useState(false);

  // ── Stage floor: which fields are required + shown by default ───────────────
  const floorMode = floorFieldKeys !== undefined;
  const req = new Set(floorFieldKeys ?? []);
  const reqAddress = req.has("mailingAddress") || req.has("legalAddress");
  const reqEmail = req.has("emailAddress") || req.has("email");
  const reqPhone =
    req.has("phones") || req.has("primaryPhone") || req.has("phone");
  const reqEntity = req.has("entityType");
  const reqPersonnel = req.has("personnel");
  // The county of each rated location (PMS-S660's `exposures.locationId.county`).
  // A payroll row the portal writes names no location, so PMS rates it at the
  // mailing address — that is the county the quote needs.
  const reqCounty = [...req].some(isLocationCountyKey);
  // The address anchors the primary surface when it's a floor field (or in
  // legacy mode); a non-floor address drops into the disclosure.
  const showAddress = !floorMode || reqAddress || reqCounty;

  // Load the pickers. Industry/CMS always; the roster only when a servicing
  // broker is being chosen (edit). A failed catalog isn't fatal — the field just
  // stays empty and the server-side requirement surfaces on submit.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [ind, cms] = await Promise.all([
          listIndustries(authedRequest, { limit: 100 }),
          listConstructionManagementSystems(authedRequest, { limit: 100 }),
        ]);
        if (cancelled) return;
        setIndustries(ind.items.map((i) => ({ value: i.id, label: i.name })));
        setCmsOptions(cms.items.map((c) => ({ value: c.id, label: c.name })));
      } catch {
        /* non-fatal */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authedRequest]);

  useEffect(() => {
    if (!withServicingBroker) return;
    let cancelled = false;
    (async () => {
      try {
        const result = await listBrokerageBrokers(authedRequest, {
          limit: 100,
        });
        if (!cancelled) {
          setBrokerOptions(
            result.items.map((b) => ({
              value: b.id,
              label: `${b.firstName} ${b.lastName}`,
            })),
          );
        }
      } catch {
        /* non-fatal — the picker stays empty */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authedRequest, withServicingBroker]);

  function setPerson(
    i: number,
    key: "firstName" | "lastName" | "title" | "email",
    value: string,
  ) {
    review.touch("personnel");
    setPersonnel((rows) =>
      rows.map((r, idx) => (idx === i ? { ...r, [key]: value } : r)),
    );
  }

  function setPersonPhones(i: number, next: PhoneValue[]) {
    review.touch("personnel");
    setPersonnel((rows) =>
      rows.map((r, idx) => (idx === i ? { ...r, phones: next } : r)),
    );
  }

  // Roles are a set, kept in canonical order so the payload is stable and never
  // carries a duplicate (PMS 400s those).
  function togglePersonRole(i: number, role: PersonnelRole, on: boolean) {
    review.touch("personnel");
    setPersonnel((rows) =>
      rows.map((r, idx) =>
        idx === i
          ? {
              ...r,
              roles: normalizeRoles(
                on ? [...r.roles, role] : r.roles.filter((x) => x !== role),
              ),
            }
          : r,
      ),
    );
  }

  // Per-row rejection reasons, or undefined before the first submit attempt.
  const phoneErrors = attempted ? phoneRowErrors(phones) : undefined;
  const personPhoneErrors = (i: number) =>
    attempted ? phoneRowErrors(personnel[i].phones) : undefined;

  // Build a PARTIAL payload (BRK-S057): required fields plus only the optional
  // fields the broker actually filled in. A blank optional is OMITTED — never
  // "" (the server 400s empty strings) and never fabricated. A partial PUT
  // leaves omitted fields unchanged server-side.
  function buildInput(): AccountInput {
    const mail = mailingSame ? legal : mailing;
    const named = personnel.filter(
      (p) => p.firstName.trim() && p.lastName.trim(),
    );
    const rosterIds = new Set(named.flatMap((p) => (p.id ? [p.id] : [])));
    const people: PersonnelInput[] = named.map((p) => {
      const personPhones = toPhoneInputs(p.phones);
      return {
        // Identity first: who this entry IS, before what it says about them.
        // Omitted for a person the broker added (they have no id yet).
        ...(p.id ? { id: p.id } : {}),
        firstName: p.firstName.trim(),
        lastName: p.lastName.trim(),
        ...(p.title?.trim() ? { title: p.title.trim() } : {}),
        ...(p.email?.trim() ? { email: p.email.trim() } : {}),
        // Omitted when the person has no numbers. The PUT replaces each person
        // wholesale, so an absent `phones` means "no numbers", not "leave
        // them alone" (the phones this form loaded are always sent back).
        ...(personPhones.length ? { phones: personPhones } : {}),
        // Same for roles (PMS-S178): absent means the person holds none.
        ...(p.roles.length ? { roles: p.roles } : {}),
        // Carried straight back out — see PersonForm.ownershipPercentage.
        ...(p.ownershipPercentage != null
          ? { ownershipPercentage: p.ownershipPercentage }
          : {}),
        // Everything else it loaded but never shows — see PersonForm.carried.
        ...carriedInput(p.carried, rosterIds),
      };
    });
    const accountPhones = toPhoneInputs(phones);
    return {
      legalName: legalName.trim(),
      ...(dbaName.trim() ? { dbaName: dbaName.trim() } : {}),
      ...(email.trim() ? { emailAddress: email.trim() } : {}),
      ...(accountPhones.length ? { phones: accountPhones } : {}),
      ...(isCompleteAddress(legal)
        ? {
            legalAddress: accountAddress(
              legal,
              ACCOUNT_FORM_ADDRESS,
              initial?.legalAddress,
            ),
          }
        : {}),
      // The mailing address may BE the legal one ("same as legal"), so it can
      // inherit line 2 / county from either loaded address it still matches.
      ...(isCompleteAddress(mail)
        ? {
            mailingAddress: accountAddress(
              mail,
              ACCOUNT_FORM_ADDRESS,
              initial?.mailingAddress,
              mailingSame ? initial?.legalAddress : null,
            ),
          }
        : {}),
      ...(people.length ? { personnel: people } : {}),
      ...(industryId ? { industryId } : {}),
      ...(ein.trim() ? { ein: ein.trim() } : {}),
      ...(website.trim() ? { website: website.trim() } : {}),
      ...(entityType ? { entityType: entityType as EntityType } : {}),
      ...(cmsId ? { constructionManagementSystemId: cmsId } : {}),
      ...(withServicingBroker && servicingBrokerId
        ? { brokerId: servicingBrokerId }
        : {}),
    };
  }

  // Client-side floor validation. Floor mode checks exactly the manifest floor
  // keys; legacy mode keeps the BRK-S046 full floor. Either way it's one handled
  // inline message (the form is noValidate — no competing native bubbles).
  function validate(input: AccountInput): string | null {
    if (!legalName.trim()) return "Enter the insured’s legal name.";
    if (floorMode) {
      if ((reqAddress || reqCounty) && !input.legalAddress)
        return "Complete the address (street, city, state, and ZIP).";
      if (reqCounty && !input.mailingAddress?.county)
        return mailingSame
          ? "Enter the county for the address."
          : "Enter the county for the mailing address.";
      if (reqEmail && !input.emailAddress) return "Enter an email address.";
      if (reqPhone && !input.phones?.length)
        return "Add at least one phone number, and mark one as primary.";
      if (reqEntity && !input.entityType) return "Select the entity type.";
      if (reqPersonnel && !input.personnel?.length)
        return "Add at least one person (first and last name).";
      return null;
    }
    return (
      accountFloorError(input) ??
      (!mailingSame && !isCompleteAddress(mailing)
        ? "Complete the mailing address, or mark it the same as the legal address."
        : null)
    );
  }

  async function persist(input: AccountInput) {
    setPending(true);
    setError(null);
    setFieldErrors(null);
    setMismatch(null);
    try {
      await onSubmit(input);
    } catch (err) {
      // A structured field error (PMS-S144 shape) renders as a per-field
      // checklist; anything else falls back to the single handled message.
      const missing = missingFieldsOf(err);
      if (missing) setFieldErrors(missing);
      else setError(messageFor(err));
    } finally {
      setPending(false);
    }
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setAttempted(true);
    const input = buildInput();
    // A number PMS would reject is caught here, with the reason inline on the
    // offending row (BRK-S073) — no round-trip to learn "US phone numbers only".
    const badPhones = [
      ...phoneRowErrors(phones),
      ...personnel.flatMap((p) => phoneRowErrors(p.phones)),
    ].filter((reason): reason is string => Boolean(reason));
    if (badPhones.length) {
      setError(
        badPhones.length === 1
          ? badPhones[0]
          : "Fix the phone numbers marked below.",
      );
      setFieldErrors(null);
      return;
    }
    const floor = validate(input);
    if (floor) {
      setError(floor);
      setFieldErrors(null);
      return;
    }
    // State-mismatch gate (BRK-S059): if the entered address state differs from
    // the step-1 intake state, hold for an explicit confirmation before saving.
    const bad = confirmStateAgainst
      ? mismatchedState(input, confirmStateAgainst)
      : null;
    if (bad) {
      setError(null);
      setFieldErrors(null);
      setMismatch({
        entered: bad,
        expected: confirmStateAgainst!.trim().toUpperCase(),
      });
      return;
    }
    void persist(input);
  }

  const legalNameField = (
    <div className="flex flex-col gap-1">
      <TextField
        label="Legal name"
        value={legalName}
        onChange={(e) => {
          setLegalName(e.target.value);
          review.touch("legalName");
        }}
        required
      />
      {review.isPending("legalName") && <ExtractedMarker />}
    </div>
  );

  // Each business field is a keyed node so PLACEMENT can follow the floor
  // (BRK-S057 fix): in floor mode a field whose key is in `floorFieldKeys`
  // renders on the primary surface with its required marker; the rest sit in the
  // "Add more detail" disclosure. In legacy mode every node is primary. `req`
  // holds the manifest keys; the `email`/`phone` aliases match either spelling.
  const businessFieldNodes: {
    key: string;
    aliases?: string[];
    node: ReactNode;
  }[] = [
    {
      key: "dbaName",
      node: (
        <TextField
          key="dbaName"
          label="DBA name"
          value={dbaName}
          onChange={(e) => {
            setDbaName(e.target.value);
            review.touch("dbaName");
          }}
          optional
        />
      ),
    },
    {
      key: "emailAddress",
      aliases: ["email"],
      node: (
        <TextField
          key="emailAddress"
          label="Email"
          type="email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            review.touch("emailAddress");
          }}
          required={!floorMode || reqEmail}
          optional={floorMode && !reqEmail}
        />
      ),
    },
    {
      key: "phones",
      // The pre-PMS-S173 spellings, so an older manifest floor key still lands.
      aliases: ["primaryPhone", "phone"],
      node: (
        <PhoneListField
          key="phones"
          label="Phone"
          value={phones}
          onChange={(rows) => {
            setPhones(rows);
            review.touch("phones");
          }}
          errors={phoneErrors}
          required={!floorMode || reqPhone}
        />
      ),
    },
    {
      key: "industryId",
      node: (
        <SelectField
          key="industryId"
          label="Industry"
          placeholder="Select an industry"
          options={industries}
          value={industryId}
          onChange={(e) => setIndustryId(e.target.value)}
          optional
        />
      ),
    },
    {
      key: "entityType",
      node: (
        <SelectField
          key="entityType"
          label="Entity type"
          placeholder="Select…"
          options={ENTITY_OPTIONS}
          value={entityType}
          onChange={(e) => {
            setEntityType(e.target.value);
            review.touch("entityType");
          }}
          required={floorMode && reqEntity}
          optional={!floorMode || !reqEntity}
        />
      ),
    },
    {
      key: "constructionManagementSystemId",
      node: (
        <SelectField
          key="constructionManagementSystemId"
          label="Construction management system"
          placeholder="None"
          options={cmsOptions}
          value={cmsId}
          onChange={(e) => setCmsId(e.target.value)}
          optional
        />
      ),
    },
    {
      key: "ein",
      node: (
        <TextField
          key="ein"
          label="EIN"
          value={ein}
          onChange={(e) => {
            setEin(e.target.value);
            review.touch("ein");
          }}
          optional
        />
      ),
    },
    {
      key: "website",
      node: (
        <TextField
          key="website"
          label="Website"
          // D-10 rule 1: the control matches the type, so a URL field gets the
          // browser's own affordances and the right keyboard on a phone.
          type="url"
          value={website}
          onChange={(e) => {
            setWebsite(e.target.value);
            review.touch("website");
          }}
          optional
        />
      ),
    },
  ];
  const isFloorField = (f: { key: string; aliases?: string[] }) =>
    floorMode && (req.has(f.key) || (f.aliases ?? []).some((a) => req.has(a)));
  // Wrap each field node with its extracted-review marker (BRK-S038); no-op when
  // the field wasn't document-prefilled.
  const withMarker = (f: { key: string; node: ReactNode }) => (
    <div key={f.key} className="flex flex-col gap-1">
      {f.node}
      {review.isPending(f.key) && <ExtractedMarker />}
    </div>
  );
  const primaryBusinessNodes = businessFieldNodes
    .filter((f) => !floorMode || isFloorField(f))
    .map(withMarker);
  const disclosureBusinessNodes = businessFieldNodes
    .filter((f) => floorMode && !isFloorField(f))
    .map(withMarker);

  const servicingBrokerBlock = withServicingBroker && (
    <div className="flex flex-col gap-4 rounded-lg border border-brk-border bg-brk-panel p-5">
      <h2 className="text-heading">Servicing broker</h2>
      <div className="flex flex-col gap-1.5">
        <SelectField
          label="Servicing broker"
          placeholder="Unassigned"
          options={brokerOptions}
          value={servicingBrokerId}
          onChange={(e) => setServicingBrokerId(e.target.value)}
        />
        {identity && servicingBrokerId !== identity.brokerId && (
          <button
            type="button"
            onClick={() => setServicingBrokerId(identity.brokerId)}
            className="self-start text-sm text-brk-accent hover:underline"
          >
            Assign to me
          </button>
        )}
      </div>
    </div>
  );

  const mailingBlock = (
    <div className="flex flex-col gap-4 rounded-lg border border-brk-border bg-brk-panel p-5">
      <label className="flex items-center gap-2 text-sm text-brk-text">
        <input
          type="checkbox"
          checked={mailingSame}
          onChange={(e) => setMailingSame(e.target.checked)}
        />
        Mailing address is the same as the legal address
      </label>
      {!mailingSame && (
        <AddressFieldset
          title="Mailing address"
          value={mailing}
          onChange={setMailing}
          bare
          withCounty
          countyRequired={reqCounty}
        />
      )}
    </div>
  );

  // The legal address as a block plus its extracted-review marker (BRK-S038),
  // reused across the floor + legacy layouts so the marker + touch live once.
  const legalAddressBlock = (title: string) => (
    <div className="flex flex-col gap-1">
      <AddressFieldset
        title={title}
        value={legal}
        onChange={(a) => {
          setLegal(a);
          review.touch("legalAddress");
        }}
        withCounty
        countyRequired={reqCounty && mailingSame}
      />
      {review.isPending("legalAddress") && <ExtractedMarker />}
    </div>
  );

  const personnelBlock = (
    <div className="flex flex-col gap-4 rounded-lg border border-brk-border bg-brk-panel p-5">
      <div className="flex items-center justify-between">
        <div className="flex flex-col">
          <h2 className="text-heading">Personnel</h2>
          {review.isPending("personnel") && <ExtractedMarker />}
        </div>
        <button
          type="button"
          onClick={() => {
            review.touch("personnel");
            setPersonnel((p) => [...p, { ...EMPTY_PERSON }]);
          }}
          className="text-sm text-brk-accent hover:underline"
        >
          + Add person
        </button>
      </div>
      {personnel.map((person, i) => (
        <div
          key={i}
          className="flex flex-col gap-3 border-b border-brk-border pb-4 last:border-0 last:pb-0"
        >
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <TextField
              label="First name"
              value={person.firstName}
              onChange={(e) => setPerson(i, "firstName", e.target.value)}
              required={i === 0 && (!floorMode || reqPersonnel)}
              optional={i !== 0 || (floorMode && !reqPersonnel)}
            />
            <TextField
              label="Last name"
              value={person.lastName}
              onChange={(e) => setPerson(i, "lastName", e.target.value)}
              required={i === 0 && (!floorMode || reqPersonnel)}
              optional={i !== 0 || (floorMode && !reqPersonnel)}
            />
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <TextField
              label="Title"
              value={person.title ?? ""}
              onChange={(e) => setPerson(i, "title", e.target.value)}
              optional
            />
            <TextField
              label="Email"
              type="email"
              value={person.email ?? ""}
              onChange={(e) => setPerson(i, "email", e.target.value)}
              optional
            />
          </div>
          {/* A person's numbers are an array too (PMS-S173) — same editor as the
              account's, on its own row so the number + line type + primary +
              remove controls have the width they need at 375px. */}
          <PhoneListField
            label="Phone"
            value={person.phones}
            onChange={(rows) => setPersonPhones(i, rows)}
            errors={personPhoneErrors(i)}
          />
          {/* Roles (PMS-S178) — checkboxes, not a select, because they overlap:
              one person is routinely both the owner and the accounting contact.
              This is what the account's separate Contacts section became. */}
          <fieldset className="flex flex-col gap-1.5">
            <legend className="text-sm font-medium text-brk-text">
              Roles <span className="font-normal text-brk-dim">(optional)</span>
            </legend>
            <div className="flex flex-wrap gap-2">
              {PERSONNEL_ROLES.map((role) => {
                const checked = person.roles.includes(role);
                return (
                  <label
                    key={role}
                    className={`flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm transition-colors ${
                      checked
                        ? "border-brk-accent bg-brk-accent/5 text-brk-text"
                        : "border-brk-border text-brk-dim hover:border-brk-accent/60"
                    }`}
                  >
                    <input
                      type="checkbox"
                      className="h-3.5 w-3.5"
                      checked={checked}
                      onChange={(e) =>
                        togglePersonRole(i, role, e.target.checked)
                      }
                    />
                    {PERSONNEL_ROLE_LABELS[role]}
                  </label>
                );
              })}
            </div>
          </fieldset>
          {personnel.length > 1 && (
            <button
              type="button"
              onClick={() => {
                review.touch("personnel");
                setPersonnel((p) => p.filter((_, idx) => idx !== i));
              }}
              className="self-start text-sm text-brk-danger hover:underline"
            >
              Remove
            </button>
          )}
        </div>
      ))}
    </div>
  );

  // Legacy mode: the full multi-card form, every section visible (unchanged
  // layout). Floor mode: a minimal primary surface (legal name + floor address)
  // with everything else behind "Add more detail".
  return (
    <form className="flex flex-col gap-5" noValidate onSubmit={handleSubmit}>
      <ExtractedReviewBanner count={review.pendingCount} />
      {fieldErrors && (
        <div
          role="alert"
          className="flex flex-col gap-1 rounded-md border border-brk-danger/40 bg-brk-danger/5 px-3 py-2 text-sm text-brk-text"
        >
          <p className="font-medium">Please add a few more details:</p>
          <ul className="list-inside list-disc text-brk-dim">
            {fieldErrors.map((f) => (
              <li key={f.key}>{f.label}</li>
            ))}
          </ul>
        </div>
      )}

      {floorMode ? (
        <>
          <div className="flex flex-col gap-4 rounded-lg border border-brk-border bg-brk-panel p-5">
            <h2 className="text-heading">Business</h2>
            {legalNameField}
            {primaryBusinessNodes}
          </div>
          {showAddress && legalAddressBlock("Address")}
          {/* The quote's county lives on the mailing address, so a floor that
              asks for it puts the mailing block on the main surface. */}
          {reqCounty && mailingBlock}
          {reqPersonnel && personnelBlock}
          {(disclosureBusinessNodes.length > 0 ||
            withServicingBroker ||
            !showAddress ||
            !reqPersonnel) &&
            (!showExtras ? (
              <button
                type="button"
                onClick={() => setShowExtras(true)}
                className="self-start text-sm text-brk-accent hover:underline"
              >
                + Add more detail
              </button>
            ) : (
              <>
                {disclosureBusinessNodes.length > 0 && (
                  <div className="flex flex-col gap-4 rounded-lg border border-brk-border bg-brk-panel p-5">
                    {disclosureBusinessNodes}
                  </div>
                )}
                {servicingBrokerBlock}
                {!showAddress && legalAddressBlock("Address")}
                {!reqCounty && mailingBlock}
                {!reqPersonnel && personnelBlock}
              </>
            ))}
        </>
      ) : (
        <>
          <div className="flex flex-col gap-4 rounded-lg border border-brk-border bg-brk-panel p-5">
            <h2 className="text-heading">Business</h2>
            {legalNameField}
            {primaryBusinessNodes}
          </div>
          {servicingBrokerBlock}
          {legalAddressBlock("Legal address")}
          {mailingBlock}
          {personnelBlock}
        </>
      )}

      {error && (
        <p role="alert" className="text-sm text-brk-danger">
          {error}
        </p>
      )}

      {mismatch && (
        <div
          role="alertdialog"
          aria-label="Confirm state mismatch"
          className="flex flex-col gap-3 rounded-md border border-brk-danger/40 bg-brk-danger/5 px-4 py-3 text-sm text-brk-text"
        >
          <p>
            The address state you entered (
            <span className="font-semibold">{mismatch.entered}</span>) doesn’t
            match the state from step 1 (
            <span className="font-semibold">{mismatch.expected}</span>). Rates
            and appetite depend on the state — continue with{" "}
            <span className="font-semibold">{mismatch.entered}</span>, or go
            back and fix it?
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => void persist(buildInput())}
              className={buttonClass()}
            >
              Continue with {mismatch.entered}
            </button>
            <button
              type="button"
              onClick={() => setMismatch(null)}
              className="text-sm text-brk-accent hover:underline"
            >
              Go back and fix it
            </button>
          </div>
        </div>
      )}

      <div className="flex items-center gap-4">
        <button type="submit" disabled={pending} className={buttonClass()}>
          {pending ? pendingLabel : submitLabel}
        </button>
        {cancelSlot}
      </div>
    </form>
  );
}

// The first entered address state (legal or mailing) that disagrees with the
// step-1 intake state (BRK-S059), or null when everything matches / no address
// state was entered. Case-insensitive; compares only the states actually going
// on the wire (buildInput omits incomplete addresses).
function mismatchedState(input: AccountInput, expected: string): string | null {
  const exp = expected.trim().toUpperCase();
  if (!exp) return null;
  const states = [input.legalAddress?.state, input.mailingAddress?.state]
    .map((s) => s?.trim().toUpperCase())
    .filter((s): s is string => Boolean(s));
  return states.find((s) => s !== exp) ?? null;
}
